import { useEffect, useState } from "react";
import { api } from "../../lib/api";

type Tab = "roster" | "metrics" | "activity";

const TIER_COLOR: Record<string, string> = {
  root: "#A8DADC", rise: "#2A9D8F", radiate: "#F4A261",
};

function ywiColor(v: number | null): string {
  if (v == null) return "#9aa";
  if (v >= 66) return "#2A9D8F";
  if (v >= 40) return "#F4A261";
  return "#E76F51";
}
function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}
function daysAgo(iso: string | null): string {
  if (!iso) return "never";
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400_000);
  if (d <= 0) return "today";
  if (d === 1) return "1 day ago";
  if (d < 30) return `${d} days ago`;
  return fmtDate(iso);
}

export function OpsPortal() {
  const [tab, setTab] = useState<Tab>("roster");

  return (
    <div className="h-full min-h-0 overflow-y-auto bg-d_card/20 p-4">
      <div className="max-w-[1100px] mx-auto">
        {/* Header */}
        <div className="mb-4">
          <div className="text-[10px] tracking-[2px] uppercase text-d_accent font-bold mb-0.5">Operations</div>
          <h1 className="font-serif text-[26px] font-semibold text-d_text leading-tight">Studio Reporting</h1>
          <p className="text-[12px] text-d_text3 mt-0.5">Internal — members, business metrics, and activity across Yvree.</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-4 border-b border-d_border/20">
          {([["roster", "Roster"], ["metrics", "Business Metrics"], ["activity", "Activity"]] as Array<[Tab, string]>).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`text-[12px] font-semibold px-3 py-2 -mb-px border-b-2 transition ${
                tab === k
                  ? "border-d_accent text-d_accent"
                  : "border-transparent text-d_text3 hover:text-d_text2"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "roster" && <RosterTab />}
        {tab === "metrics" && <MetricsTab />}
        {tab === "activity" && <ActivityTab />}
      </div>
    </div>
  );
}

// ─── ROSTER ─────────────────────────────────────────────────────────────────
function RosterTab() {
  const [roster, setRoster] = useState<any[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.ops.roster().then((r) => setRoster(r.roster)).catch((e) => setErr(e.message));
  }, []);

  if (err) return <ErrorBox msg={err} />;
  if (!roster) return <Loading />;
  if (roster.length === 0) return <Empty msg="No members yet." />;

  return (
    <div className="overflow-x-auto rounded-lg border border-d_border/20">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="bg-d_card2/40 text-d_text3 text-[10px] uppercase tracking-[0.5px]">
            <th className="text-left font-semibold px-3 py-2.5">Member</th>
            <th className="text-left font-semibold px-3 py-2.5">Tier</th>
            <th className="text-left font-semibold px-3 py-2.5">YWI</th>
            <th className="text-left font-semibold px-3 py-2.5">Wearables</th>
            <th className="text-left font-semibold px-3 py-2.5">Visits 30d</th>
            <th className="text-left font-semibold px-3 py-2.5">Last visit</th>
            <th className="text-left font-semibold px-3 py-2.5">Enrolled</th>
          </tr>
        </thead>
        <tbody>
          {roster.map((m) => (
            <tr key={m.id} className="border-t border-d_border/10 hover:bg-d_card2/30">
              <td className="px-3 py-2.5">
                <div className="font-semibold text-d_text">{m.name}</div>
                <div className="text-[10px] text-d_text3">{m.email}</div>
              </td>
              <td className="px-3 py-2.5">
                <span className="text-[10px] font-bold uppercase tracking-[0.5px] px-2 py-0.5 rounded-full"
                      style={{ background: (TIER_COLOR[m.tier] || "#ccc") + "22", color: TIER_COLOR[m.tier] || "#888" }}>
                  {m.tier}
                </span>
              </td>
              <td className="px-3 py-2.5">
                {m.ywi != null ? (
                  <div className="flex items-center gap-1.5">
                    <span className="font-serif font-bold text-[15px]" style={{ color: ywiColor(m.ywi) }}>{m.ywi.toFixed(0)}</span>
                    {m.ywi_trend && <TrendArrow dir={m.ywi_trend} />}
                  </div>
                ) : <span className="text-d_text3">—</span>}
              </td>
              <td className="px-3 py-2.5">
                {m.wearable_count > 0
                  ? <span className="text-d_text2">{m.wearables.join(", ")}</span>
                  : <span className="text-d_text3">none</span>}
              </td>
              <td className="px-3 py-2.5">
                <span className={m.visits_30d > 0 ? "text-d_text font-semibold" : "text-d_text3"}>{m.visits_30d}</span>
              </td>
              <td className="px-3 py-2.5 text-d_text2">{daysAgo(m.last_visit_at)}</td>
              <td className="px-3 py-2.5 text-d_text3">{fmtDate(m.enrolled_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── METRICS ────────────────────────────────────────────────────────────────
function MetricsTab() {
  const [m, setM] = useState<any | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { api.ops.metrics().then(setM).catch((e) => setErr(e.message)); }, []);
  if (err) return <ErrorBox msg={err} />;
  if (!m) return <Loading />;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="Active Members" value={m.members_active} sub={`${m.members_total} total`} />
        <Stat label="New (30d)" value={m.new_30d} />
        <Stat label="Avg YWI" value={m.avg_ywi != null ? m.avg_ywi.toFixed(1) : "—"} color={ywiColor(m.avg_ywi)} />
        <Stat label="Wearable Adoption" value={`${Math.round((m.wearable_adoption_rate || 0) * 100)}%`} sub={`${m.wearable_adopters} of ${m.members_active}`} />
      </div>

      <div className="rounded-lg border border-d_border/20 p-4">
        <div className="text-[10px] uppercase tracking-[1px] text-d_text3 font-bold mb-3">Tier Distribution</div>
        <div className="space-y-2">
          {["radiate", "rise", "root"].map((tier) => {
            const count = m.tier_distribution?.[tier] || 0;
            const pct = m.members_active ? (count / m.members_active) * 100 : 0;
            return (
              <div key={tier} className="flex items-center gap-3">
                <span className="text-[11px] font-semibold uppercase tracking-[0.5px] w-16" style={{ color: TIER_COLOR[tier] }}>{tier}</span>
                <div className="flex-1 h-5 bg-d_card2/50 rounded overflow-hidden">
                  <div className="h-full rounded transition-all" style={{ width: `${pct}%`, background: TIER_COLOR[tier], minWidth: count > 0 ? 24 : 0 }} />
                </div>
                <span className="text-[12px] text-d_text2 font-semibold w-8 text-right">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      <p className="text-[11px] text-d_text3">
        MRR and churn will populate once membership pricing and WellnessLiving billing are wired in.
      </p>
    </div>
  );
}

// ─── ACTIVITY ───────────────────────────────────────────────────────────────
function ActivityTab() {
  const [data, setData] = useState<any | null>(null);
  const [days, setDays] = useState(30);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setData(null); api.ops.activity(days).then(setData).catch((e) => setErr(e.message)); }, [days]);
  if (err) return <ErrorBox msg={err} />;

  return (
    <div className="space-y-4">
      <div className="flex gap-1">
        {[7, 30, 90].map((d) => (
          <button key={d} onClick={() => setDays(d)}
            className={`text-[11px] font-semibold px-2.5 py-1 rounded border transition ${
              days === d ? "bg-d_accent/10 text-d_accent border-d_accent/20" : "border-d_border/20 text-d_text3 hover:text-d_text2"
            }`}>
            {d}d
          </button>
        ))}
      </div>

      {!data ? <Loading /> : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Stat label="Total Sessions" value={data.total_sessions} />
            <Stat label="Group" value={data.group_sessions} />
            <Stat label="1:1" value={data.one_on_one_sessions} />
          </div>

          <div className="rounded-lg border border-d_border/20 p-4">
            <div className="text-[10px] uppercase tracking-[1px] text-d_text3 font-bold mb-3">By Category</div>
            {Object.keys(data.by_category || {}).length === 0 ? (
              <p className="text-[12px] text-d_text3">No sessions in this window.</p>
            ) : (
              <div className="space-y-1.5">
                {Object.entries(data.by_category)
                  .sort((a: any, b: any) => b[1] - a[1])
                  .map(([cat, n]: any) => {
                    const max = Math.max(...Object.values(data.by_category).map(Number));
                    return (
                      <div key={cat} className="flex items-center gap-3">
                        <span className="text-[11px] text-d_text2 w-32 truncate">{cat.replace(/_/g, " ")}</span>
                        <div className="flex-1 h-4 bg-d_card2/50 rounded overflow-hidden">
                          <div className="h-full bg-d_accent rounded" style={{ width: `${(n / max) * 100}%`, minWidth: 18 }} />
                        </div>
                        <span className="text-[11px] text-d_text2 font-semibold w-6 text-right">{n}</span>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

// ─── shared bits ────────────────────────────────────────────────────────────
function Stat({ label, value, sub, color }: { label: string; value: any; sub?: string; color?: string }) {
  return (
    <div className="rounded-lg border border-d_border/20 p-3 bg-d_card/40">
      <div className="text-[9px] uppercase tracking-[1px] text-d_text3 font-bold mb-1">{label}</div>
      <div className="font-serif text-[24px] font-semibold leading-none" style={color ? { color } : undefined}>{value}</div>
      {sub && <div className="text-[10px] text-d_text3 mt-1">{sub}</div>}
    </div>
  );
}
function TrendArrow({ dir }: { dir: string }) {
  if (dir === "rising" || dir === "up") return <span className="text-[10px] text-d_accent">▲</span>;
  if (dir === "declining" || dir === "down") return <span className="text-[10px] text-d_coral">▼</span>;
  return <span className="text-[10px] text-d_text3">→</span>;
}
function Loading() { return <div className="text-center py-12 text-d_text3 text-[12px] uppercase tracking-wider">Loading…</div>; }
function Empty({ msg }: { msg: string }) { return <div className="text-center py-12 text-d_text3 text-[13px]">{msg}</div>; }
function ErrorBox({ msg }: { msg: string }) {
  return <div className="rounded-lg border border-d_coral/30 bg-d_coral/5 p-4 text-[12px] text-d_coral">{msg}</div>;
}
